import React, { createContext, useEffect, useState } from "react";

export const CartContext = createContext();

const CART_STORAGE_KEY = "cartItems";
const WISHLIST_STORAGE_KEY = "wishlistItems";
const MAX_QUANTITY = 99;

const normalizeProductId = (productId) => {
  const normalizedId = Number(productId);
  return Number.isInteger(normalizedId) && normalizedId > 0
    ? normalizedId
    : null;
};

const normalizeVariant = (value) =>
  typeof value === "string" ? value.trim() : "";

const normalizeQuantity = (quantity) => {
  const normalizedQuantity = Math.floor(Number(quantity));
  return Number.isFinite(normalizedQuantity) ? normalizedQuantity : null;
};

const sanitizeCartItems = (value) => {
  if (!Array.isArray(value)) return [];

  return value.reduce((items, item) => {
    if (!item || typeof item !== "object") return items;

    const id = normalizeProductId(item.id);
    const size = normalizeVariant(item.size);
    const color = normalizeVariant(item.color);
    const quantity = normalizeQuantity(item.quantity);

    if (!id || !size || !color || !quantity || quantity < 1) return items;

    const existingItemIndex = items.findIndex(
      (currentItem) =>
        currentItem.id === id &&
        currentItem.size === size &&
        currentItem.color === color
    );

    if (existingItemIndex >= 0) {
      const nextItems = [...items];
      const existingItem = nextItems[existingItemIndex];
      nextItems[existingItemIndex] = {
        ...existingItem,
        quantity: Math.min(
          MAX_QUANTITY,
          existingItem.quantity + Math.min(quantity, MAX_QUANTITY)
        ),
      };
      return nextItems;
    }

    return [
      ...items,
      { id, size, color, quantity: Math.min(quantity, MAX_QUANTITY) },
    ];
  }, []);
};

const sanitizeWishlistItems = (value) => {
  if (!Array.isArray(value)) return [];

  return value.reduce((items, item) => {
    const id = normalizeProductId(
      item && typeof item === "object" ? item.id : item
    );

    if (!id || items.includes(id)) return items;
    return [...items, id];
  }, []);
};

const readStoredItems = (key, sanitize) => {
  if (typeof window === "undefined") return [];

  try {
    const storedValue = window.localStorage.getItem(key);
    return storedValue ? sanitize(JSON.parse(storedValue)) : [];
  } catch {
    return [];
  }
};

const persistItems = (key, value) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Browsing can continue when storage is unavailable or full.
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStoredItems(CART_STORAGE_KEY, sanitizeCartItems)
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    readStoredItems(WISHLIST_STORAGE_KEY, sanitizeWishlistItems)
  );

  useEffect(() => {
    persistItems(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    persistItems(WISHLIST_STORAGE_KEY, wishlistItems);
  }, [wishlistItems]);

  const addToCart = (productId, quantity = 1, size = "", color = "") => {
    const id = normalizeProductId(productId);
    const normalizedQuantity = normalizeQuantity(quantity);
    const normalizedSize = normalizeVariant(size);
    const normalizedColor = normalizeVariant(color);

    if (
      !id ||
      !normalizedQuantity ||
      normalizedQuantity < 1 ||
      !normalizedSize ||
      !normalizedColor
    ) {
      return;
    }

    setCartItems((previousItems) => {
      const existingItem = previousItems.find(
        (item) =>
          item.id === id &&
          item.size === normalizedSize &&
          item.color === normalizedColor
      );

      if (!existingItem) {
        return [
          ...previousItems,
          {
            id,
            size: normalizedSize,
            color: normalizedColor,
            quantity: Math.min(normalizedQuantity, MAX_QUANTITY),
          },
        ];
      }

      return previousItems.map((item) =>
        item.id === id &&
        item.size === normalizedSize &&
        item.color === normalizedColor
          ? {
              ...item,
              quantity: Math.min(
                item.quantity + normalizedQuantity,
                MAX_QUANTITY
              ),
            }
          : item
      );
    });
  };

  const removeFromCart = (productId, size = "", color = "") => {
    const id = normalizeProductId(productId);
    const normalizedSize = normalizeVariant(size);
    const normalizedColor = normalizeVariant(color);

    if (!id || !normalizedSize || !normalizedColor) return;

    setCartItems((previousItems) =>
      previousItems.filter(
        (item) =>
          !(
            item.id === id &&
            item.size === normalizedSize &&
            item.color === normalizedColor
          )
      )
    );
  };

  const updateQuantity = (productId, size, color, quantity) => {
    const id = normalizeProductId(productId);
    const normalizedSize = normalizeVariant(size);
    const normalizedColor = normalizeVariant(color);
    const normalizedQuantity = normalizeQuantity(quantity);

    if (
      !id ||
      !normalizedSize ||
      !normalizedColor ||
      normalizedQuantity === null
    ) {
      return;
    }

    setCartItems((previousItems) => {
      if (normalizedQuantity <= 0) {
        return previousItems.filter(
          (item) =>
            !(
              item.id === id &&
              item.size === normalizedSize &&
              item.color === normalizedColor
            )
        );
      }

      return previousItems.map((item) =>
        item.id === id &&
        item.size === normalizedSize &&
        item.color === normalizedColor
          ? {
              ...item,
              quantity: Math.min(normalizedQuantity, MAX_QUANTITY),
            }
          : item
      );
    });
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const addToWishlist = (productId) => {
    const id = normalizeProductId(productId);
    if (!id) return;

    setWishlistItems((previousItems) =>
      previousItems.includes(id) ? previousItems : [...previousItems, id]
    );
  };

  const removeFromWishlist = (productId) => {
    const id = normalizeProductId(productId);
    if (!id) return;

    setWishlistItems((previousItems) =>
      previousItems.filter((itemId) => itemId !== id)
    );
  };

  const toggleWishlist = (productId) => {
    const id = normalizeProductId(productId);
    if (!id) return;

    setWishlistItems((previousItems) =>
      previousItems.includes(id)
        ? previousItems.filter((itemId) => itemId !== id)
        : [...previousItems, id]
    );
  };

  const isInWishlist = (productId) => {
    const id = normalizeProductId(productId);
    return id ? wishlistItems.includes(id) : false;
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        wishlistItems,
        addToCart,
        removeFromCart,
        clearCart,
        updateQuantity,
        addToWishlist,
        removeFromWishlist,
        toggleWishlist,
        isInWishlist,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
